// Work kit — COMMENT THREAD controller (wave 9, Track B, `ui--work-comments`).
// Generalizes the W4 issue-comment composer (`ui--work-issue-composer`,
// untouched) with one nesting level, host-flagged edit/delete/resolve,
// host-curated reactions, the resolved filter, and paragraph-anchor overlay
// wiring. The W4 semantics it carries over byte-identically on the refactored
// issue surface (`threaded: false`, subject_key "issue_id"):
//
//   - draft autosave per subject id (localStorage, per-viewer convenience
//     only — same key prefix as W4), restored on connect, cleared when the
//     create is accepted,
//   - Ctrl/Cmd+Enter submits; plain Enter keeps its native newline (the text
//     editor is never reached into — see lib/text_editor),
//   - submit → optimistic card from the rendered template (textContent only —
//     never HTML inserted from user input) → emit ui--work:comment-create;
//     a reply optimistically appends under its parent thread. Every submit
//     mints a per-op `op_token` (the drag kernel's gesture_token idiom),
//     stamps its optimistic row (`data-work-comment-op`) and rides the
//     event detail — hosts echo it back on the ack,
//   - create ack ok → the optimistic row STANDS IN until the host re-render
//     swaps the list (only the failure path removes it); !ok → the FAILED
//     op's row is removed (matched by the echoed `op_token` — concurrent
//     optimistic rows for this subject stand); a tokenless (legacy W4)
//     ack cannot be attributed to an op and falls back to the coarse sweep,
//     the failed body's draft is restored, exactly one toast (the F4 seam).
//
// New in wave 9 (all events bubbling, no HTTP):
//
//   ui--work:comment-update  {<subject_key>, comment_id, body}
//   ui--work:comment-delete  {<subject_key>, comment_id}
//   ui--work:comment-resolve {<subject_key>, comment_id, resolved}
//   ui--work:comment-react   {<subject_key>, comment_id, emoji, active}
//   ui--work:comment-load-more {<subject_key>, cursor}
//
// with matching `ui--work:comment-*-ack` consumption events (`ok` adopts the
// server truth — `comment?`/`reactions?` payloads — `!ok` reverts + exactly
// one toast). The create ack keeps the W4 name `ui--work:comment-ack` and
// also accepts `ui--work:comment-create-ack`. Resolved filtering and the
// anchor overlay emit nothing (read-only UI state).
//
// Affordances render from HOST flags only (can_edit / can_delete /
// can_resolve per def) — the kit never derives who the viewer is.

import { Controller } from "@hotwired/stimulus"
import {
  clearToasts,
  emitFromIssueRoot,
  showToast,
  toastRegionFor,
} from "../../../lib/work/mutation_seam"
import {
  clearEditor,
  editorText,
  focusEditor,
  setEditorText,
} from "../../../lib/text_editor"

const CREATE_EVENT = "ui--work:comment-create"
const UPDATE_EVENT = "ui--work:comment-update"
const DELETE_EVENT = "ui--work:comment-delete"
const RESOLVE_EVENT = "ui--work:comment-resolve"
const REACT_EVENT = "ui--work:comment-react"
const LOAD_MORE_EVENT = "ui--work:comment-load-more"

// The W4 create ack keeps its historical name; the generalized acks are
// suffixed (the spec's `comment-*-ack` grammar).
const CREATE_ACK_EVENTS = ["ui--work:comment-ack", "ui--work:comment-create-ack"]
const UPDATE_ACK_EVENT = "ui--work:comment-update-ack"
const DELETE_ACK_EVENT = "ui--work:comment-delete-ack"
const RESOLVE_ACK_EVENT = "ui--work:comment-resolve-ack"
const REACT_ACK_EVENT = "ui--work:comment-react-ack"
const LOAD_MORE_ACK_EVENT = "ui--work:comment-load-more-ack"

const DRAFT_KEY_PREFIX = "conek-ui:work:comment-draft:"

// Token-pure paint — the paragraph wash (the spec's Gap-fill 2 tint) and the
// card focus ring. The classes mirror the W1 kernel's NEST_CLASSES so the
// Tailwind build already carries them.
const PARAGRAPH_WASH_CLASSES = ["bg-primary/10", "ring-2", "ring-inset", "ring-primary"]
const CARD_RING_CLASSES = ["ring-2", "ring-primary"]
const MUTED_CLASS = "opacity-60"

// Per-op token generator — the drag kernel's `newGestureToken` idiom, scoped
// to this controller (module-local counter + crypto UUID, Math.random
// fallback when crypto is unavailable). One token per submit: stamped on the
// optimistic row as `data-work-comment-op` and emitted as `op_token`, so the
// host's echoed ack reverts exactly one row instead of sweeping every
// concurrent optimistic submit.
let opCounter = 0
function newOpToken() {
  opCounter += 1
  if (globalThis.crypto?.randomUUID) {
    return `work-comment-${opCounter}-${globalThis.crypto.randomUUID()}`
  }
  return `work-comment-${opCounter}-${Math.random().toString(36).slice(2)}`
}

// The op registry is FIFO-capped (the drag kernel's snapshot-cap rationale):
// tokenless legacy acks never delete their entry, so an unbounded map would
// grow with every submit on a long-lived page.
const OP_CAP = 20

export default class extends Controller {
  static values = {
    subjectKey: String,
    subjectId: String,
    threaded: { type: Boolean, default: true },
    canComment: { type: Boolean, default: false },
    viewerName: { type: String, default: "" },
    errorMessage: { type: String, default: "" },
    updateError: { type: String, default: "" },
    deleteError: { type: String, default: "" },
    resolveError: { type: String, default: "" },
    reactError: { type: String, default: "" },
    loadMoreError: { type: String, default: "" },
    savedMessage: { type: String, default: "" },
    anchorsSelector: { type: String, default: "" },
  }

  static targets = ["commentList", "commentTemplate", "replyTemplate", "replyBanner", "replyName", "filterChip", "live", "composer", "deleteDialog", "loadMore"]

  connect() {
    this._onAck = this._handleAck.bind(this)
    this._onDocumentClick = this._handleDocumentClick.bind(this)
    this._bindAckListeners(true)
    document.addEventListener("click", this._onDocumentClick)

    // Highlight bookkeeping: element -> classes added (for exact removal).
    this._painted = new Map()
    this._replyToId = null
    this._replyToName = ""
    this._editingCard = null
    this._editBodyClone = null
    this._filterOn = false
    this._pending = new Map() // comment_id -> rollback snapshot for update/resolve/react
    this._composerTouched = false // real user input seen since the last submit/restore
    this._opBodies = new Map() // op_token -> submitted body (restored on that op's failure)
    this._pendingDeleteId = null // the comment the confirmation dialog is open for
    this._loadingMore = false

    // Deferred to a microtask: Stimulus connects a parent before its children,
    // so the composer's own ui--text-editor controller does not exist yet at
    // this point and the restore would find nothing to write into. Stimulus
    // connects a whole batch of elements synchronously, so the child is up by
    // the time this runs.
    if (this.canCommentValue) queueMicrotask(() => this._restoreDraft())
  }

  disconnect() {
    this._bindAckListeners(false)
    document.removeEventListener("click", this._onDocumentClick)
    this._clearAllPaint()
  }

  // === Composer (W4 semantics) ===

  // Editor input (ui--text-editor:change, re-dispatched by the component from
  // whichever engine it runs) → autosave.
  draftChanged() {
    this._composerTouched = true // real user input — the restore guard reads this
    this._saveDraft(this._composerText())
  }

  composerKeydown(event) {
    if (event.key !== "Enter") return
    if (!(event.metaKey || event.ctrlKey)) return
    event.preventDefault()
    this.submit()
  }

  submit() {
    if (!this.canCommentValue) return
    const body = this._composerText().trim()
    if (!body) return

    const opToken = newOpToken()
    this._rememberOp(opToken, body)
    const parentId = this._replyToId
    if (parentId) {
      this._appendOptimisticReply(body, parentId, opToken)
    } else {
      this._prependOptimistic(body, opToken)
    }
    this._clearComposer()
    this._composerTouched = false // the submit emptied the editor, not the user
    this._saveDraft("") // accepted path clears; failures restore below
    this._announce("")

    const detail = { [this.subjectKeyValue]: this.subjectIdValue, body: body, op_token: opToken }
    if (this.threadedValue) detail.parent_id = parentId
    emitFromIssueRoot(this.element, CREATE_EVENT, detail)
  }

  // === Delegated card interactions ===

  cardHover(event) {
    const card = event.target.closest("[data-work-comment-anchor]")
    if (!card || card === this._hoverCard) return
    this._hoverCard = card
    this._clearParagraphPaint()
    const paragraph = this._paragraphFor(card.getAttribute("data-work-comment-anchor"))
    if (paragraph) this._paint(paragraph, PARAGRAPH_WASH_CLASSES)
  }

  cardOut(event) {
    if (!this._hoverCard) return
    if (event.relatedTarget && this._hoverCard.contains(event.relatedTarget)) return
    this._hoverCard = null
    this._clearParagraphPaint()
  }

  cardClick(event) {
    const action = event.target.closest("[data-work-comment-action]")
    if (action) {
      this._routeAction(action, event)
      return
    }
    // Clicks into an open in-place edit shell must not scroll the page
    // away mid-edit.
    if (event.target.closest("[data-work-comment-edit]")) return
    // Clicking an anchored comment scrolls to its paragraph (hover already
    // highlighted it). Reads only — never an event.
    const anchored = event.target.closest("[data-work-comment-anchor]")
    if (anchored) {
      this._scrollTo(this._paragraphFor(anchored.getAttribute("data-work-comment-anchor")))
    }
  }

  // Root keydown: Esc cancels an in-place edit (byte-identical DOM, no
  // event); Ctrl/Cmd+Enter saves the edit; Esc clears reply mode.
  keydown(event) {
    const editWrapper = event.target.closest("[data-work-comment-edit]")
    if (this._editingCard) {
      if (event.key === "Escape") {
        event.preventDefault()
        this._cancelEdit()
      } else if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        const save = this._editingCard.querySelector("[data-work-comment-action='save-edit']")
        if (save) save.click()
      }
      return
    }
    if (editWrapper) return // edit shell not open — nothing to do

    if (event.key === "Escape" && this._replyToId) {
      event.preventDefault()
      this._clearReply()
    }
  }

  // === Resolved filter (read-only UI state — no event, no re-fetch) ===

  toggleFilter() {
    this._filterOn = !this._filterOn
    if (this.hasFilterChipTarget) {
      this.filterChipTarget.setAttribute("aria-pressed", this._filterOn ? "true" : "false")
      this.filterChipTarget.classList.toggle("border-primary", this._filterOn)
      this.filterChipTarget.classList.toggle("bg-primary/10", this._filterOn)
      this.filterChipTarget.classList.toggle("text-primary", this._filterOn)
    }
    this._cards().forEach((card) => {
      if (card.getAttribute("data-work-comment-resolved") !== "true") return
      card.classList.toggle("hidden", this._filterOn)
    })
  }

  // === internals: action routing ===

  _routeAction(action, event) {
    const kind = action.getAttribute("data-work-comment-action")
    const li = action.closest("li[data-work-comment-id]")
    const id = li ? li.getAttribute("data-work-comment-id") : null
    if (kind === "cancel-reply") {
      event.stopPropagation()
      this._clearReply()
      return
    }
    // The confirmation dialog is rendered ONCE for the whole thread, so its
    // buttons sit outside every comment <li> and carry no id of their own —
    // the id they act on is the one Delete was clicked for.
    if (kind === "cancel-delete") {
      event.stopPropagation()
      this._closeDeleteDialog()
      return
    }
    if (kind === "confirm-delete") {
      event.stopPropagation()
      this._confirmDelete()
      return
    }
    if (!id) return
    switch (kind) {
      case "reply":
        event.stopPropagation()
        this._startReply(li, id)
        break
      case "edit":
        event.stopPropagation()
        this._startEdit(li)
        break
      case "cancel-edit":
        event.stopPropagation()
        this._cancelEdit()
        break
      case "save-edit":
        event.stopPropagation()
        this._saveEdit(li, id)
        break
      case "delete":
        event.stopPropagation()
        this._delete(li, id)
        break
      case "resolve":
        event.stopPropagation()
        this._toggleResolve(li, id)
        break
      case "react": {
        event.stopPropagation()
        this._react(li, id, action)
        break
      }
    }
  }

  // === internals: reply state ===

  _startReply(li, id) {
    if (!this.canCommentValue) return
    const rootId = li.getAttribute("data-work-comment-root-id") || id
    this._replyToId = rootId
    const author = li.querySelector("[data-work-comment-part='author']")
    this._replyToName = author ? author.textContent : ""
    if (this.hasReplyBannerTarget) this.replyBannerTarget.hidden = false
    if (this.hasReplyNameTarget) this.replyNameTarget.textContent = this._replyToName
    this._focusComposer()
  }

  _clearReply() {
    this._replyToId = null
    this._replyToName = ""
    if (this.hasReplyBannerTarget) this.replyBannerTarget.hidden = true
    if (this.hasReplyNameTarget) this.replyNameTarget.textContent = ""
  }

  // === internals: edit in place ===

  _startEdit(li) {
    if (this._editingCard) this._cancelEdit()
    const editWrapper = li.querySelector("[data-work-comment-edit]")
    const body = li.querySelector("[data-work-comment-part='body']")
    if (!editWrapper || !body) return

    this._editingCard = li
    this._editBodyClone = body.cloneNode(true)
    body.hidden = true
    const actions = li.querySelector("[data-work-comment-actions]")
    if (actions) actions.hidden = true
    editWrapper.hidden = false
    focusEditor(this.application, editWrapper)
  }

  // Esc: restore the pre-edit state by toggling back — no DOM surgery, no
  // event, no toast. The body text was never modified, so the card DOM is
  // byte-identical to pre-edit.
  _cancelEdit() {
    const li = this._editingCard
    if (!li) return
    const editWrapper = li.querySelector("[data-work-comment-edit]")
    const body = li.querySelector("[data-work-comment-part='body']")
    if (editWrapper) editWrapper.hidden = true
    if (body) body.hidden = false
    const actions = li.querySelector("[data-work-comment-actions]")
    if (actions) actions.hidden = false
    this._editingCard = null
    this._editBodyClone = null
  }

  _saveEdit(li, id) {
    const editWrapper = li.querySelector("[data-work-comment-edit]")
    if (!editWrapper) return
    const body = li.querySelector("[data-work-comment-part='body']")
    if (!body) return

    const text = this._editorText(editWrapper).trim()
    if (!text) return

    // Optimistic apply in place (textContent — never HTML from user input).
    // The original server-rendered body NODE is kept for the failure revert
    // (restored by node replacement — never innerHTML).
    body.textContent = text
    editWrapper.hidden = true
    body.hidden = false
    const actions = li.querySelector("[data-work-comment-actions]")
    if (actions) actions.hidden = false

    this._pending.set(id, { bodyClone: this._editBodyClone })
    this._editingCard = null
    this._editBodyClone = null

    emitFromIssueRoot(this.element, UPDATE_EVENT, {
      [this.subjectKeyValue]: this.subjectIdValue,
      comment_id: id,
      body: text,
    })
  }

  // === internals: delete / resolve / react ===

  // Delete is a two-step now: the row's button only asks. A host that renders
  // no dialog (nothing deletable, or a template from before this) keeps the
  // one-click behaviour rather than ending up with a button that does nothing.
  _delete(li, id) {
    if (!this.hasDeleteDialogTarget) {
      this._performDelete(li, id)
      return
    }
    this._pendingDeleteId = id
    this._openDeleteDialog()
  }

  _confirmDelete() {
    const id = this._pendingDeleteId
    this._closeDeleteDialog()
    if (!id) return
    const li = this._liById(id)
    if (li) this._performDelete(li, id)
  }

  _performDelete(li, id) {
    // Optimistic hide (the F4 grammar): the row leaves the view now; the ok
    // ack removes it for good, a failed ack restores it + exactly one toast.
    li.setAttribute("data-work-comment-removed", "")
    li.classList.add("hidden")
    emitFromIssueRoot(this.element, DELETE_EVENT, {
      [this.subjectKeyValue]: this.subjectIdValue,
      comment_id: id,
    })
  }

  // Through the ui--modal controller when it is on the dialog (it owns focus
  // restoration and the animated close), natively otherwise — the kit's own
  // vitest harness mounts the dialog without it.
  _openDeleteDialog() {
    const dialog = this.deleteDialogTarget
    const modal = this._modalControllerFor(dialog)
    if (modal) return modal.open()
    if (typeof dialog.showModal === "function") dialog.showModal()
    else dialog.setAttribute("open", "")
  }

  _closeDeleteDialog() {
    this._pendingDeleteId = null
    if (!this.hasDeleteDialogTarget) return
    const dialog = this.deleteDialogTarget
    const modal = this._modalControllerFor(dialog)
    if (modal) return modal.close()
    if (typeof dialog.close === "function") dialog.close()
    else dialog.removeAttribute("open")
  }

  _modalControllerFor(dialog) {
    return this.application?.getControllerForElementAndIdentifier?.(dialog, "ui--modal") || null
  }

  // === internals: host-paginated history ===

  // The kit owns no page state: it hands back the cursor the host put on the
  // button and waits. One load at a time — a second click while one is in
  // flight would ask the host for the same page twice.
  loadMore() {
    if (this._loadingMore || !this.hasLoadMoreTarget) return
    this._loadingMore = true
    const button = this.loadMoreTarget
    button.disabled = true
    button.setAttribute("aria-busy", "true")
    emitFromIssueRoot(this.element, LOAD_MORE_EVENT, {
      [this.subjectKeyValue]: this.subjectIdValue,
      cursor: button.getAttribute("data-work-comment-cursor") || "",
    })
  }

  _loadMoreAck(detail, region) {
    this._loadingMore = false
    if (!this.hasLoadMoreTarget) return
    const button = this.loadMoreTarget
    button.disabled = false
    button.removeAttribute("aria-busy")

    if (detail.ok === false) {
      clearToasts(region)
      showToast(region, this.loadMoreErrorValue, "error")
      return
    }
    // The host re-renders the list itself; all that is left here is whether
    // there is another page to ask for.
    if (detail.has_more === false) {
      button.remove()
      return
    }
    if (detail.next_cursor != null) {
      button.setAttribute("data-work-comment-cursor", String(detail.next_cursor))
    }
  }

  _toggleResolve(li, id) {
    const resolved = li.getAttribute("data-work-comment-resolved") !== "true"
    const snapshot = this._resolveSnapshot(li)
    // Single-flight baseline: the pending snapshot is the ONLY truthful
    // rollback target — it was read BEFORE the first optimistic apply. A
    // second toggle while the ack is pending re-reads the already-applied
    // DOM, so overwriting the entry would compose the optimistic state into
    // the baseline and a failing ack would "revert" to it (the toggle
    // sticks). The toggle itself stays live — it still applies + emits — it
    // just never rewrites the pending snapshot.
    if (!this._pending.has(`resolve:${id}`)) this._pending.set(`resolve:${id}`, snapshot)
    this._applyResolved(li, resolved, snapshot.button)
    emitFromIssueRoot(this.element, RESOLVE_EVENT, {
      [this.subjectKeyValue]: this.subjectIdValue,
      comment_id: id,
      resolved: resolved,
    })
  }

  _react(li, id, button) {
    const emoji = button.getAttribute("data-work-comment-emoji") || ""
    const wasActive = button.getAttribute("aria-pressed") === "true"
    // Single-flight baseline (same rationale as _toggleResolve): keep the
    // FIRST snapshot for this comment+emoji — never overwrite it with the
    // already-optimistic button state.
    const key = `react:${id}:${emoji}`
    if (!this._pending.has(key)) {
      this._pending.set(key, {
        count: button.getAttribute("data-work-comment-count"),
        pressed: button.getAttribute("aria-pressed"),
      })
    }
    this._applyReaction(button, !wasActive)
    emitFromIssueRoot(this.element, REACT_EVENT, {
      [this.subjectKeyValue]: this.subjectIdValue,
      comment_id: id,
      emoji: emoji,
      active: !wasActive,
    })
  }

  // === internals: optimistic rows (W4 semantics) ===

  // Optimistic top-level card: cloned from the ERB template (kit markup,
  // safe), filled with textContent only. `opToken` stamps the row so the
  // failure path can revert exactly this op.
  _prependOptimistic(body, opToken) {
    if (!this.hasCommentTemplateTarget || !this.hasCommentListTarget) return
    const node = this.commentTemplateTarget.content.firstElementChild
    if (!node) return
    const clone = node.cloneNode(true)
    clone.setAttribute("data-work-comment-optimistic", "")
    clone.setAttribute("data-work-comment-op", opToken)
    this._fillTemplate(clone, body)
    this.commentListTarget.prepend(clone)
  }

  _appendOptimisticReply(body, rootId, opToken) {
    if (!this.hasReplyTemplateTarget) return
    // Iteration lookup — the root id is host data, never interpolated into a
    // selector.
    const replies = Array.from(this.element.querySelectorAll("[data-work-comment-replies]"))
      .find((node) => node.getAttribute("data-work-comment-replies") === rootId)
    if (!replies) return
    const node = this.replyTemplateTarget.content.firstElementChild
    if (!node) return
    const clone = node.cloneNode(true)
    clone.setAttribute("data-work-comment-optimistic", "")
    clone.setAttribute("data-work-comment-op", opToken)
    this._fillTemplate(clone, body)
    replies.hidden = false
    replies.appendChild(clone)
  }

  _fillTemplate(clone, body) {
    const part = (name) => clone.querySelector(`[data-work-comment-part='${name}']`)
    const timestamp = part("timestamp")
    if (timestamp) timestamp.textContent = this._nowLabel()
    const author = part("author")
    if (author) author.textContent = this.viewerNameValue
    const bodyPart = part("body")
    if (bodyPart) bodyPart.textContent = body
  }

  // === internals: ack seam ===

  _bindAckListeners(add) {
    const all = [...CREATE_ACK_EVENTS, UPDATE_ACK_EVENT, DELETE_ACK_EVENT, RESOLVE_ACK_EVENT, REACT_ACK_EVENT,
      LOAD_MORE_ACK_EVENT]
    all.forEach((name) => {
      if (add) document.addEventListener(name, this._onAck)
      else document.removeEventListener(name, this._onAck)
    })
  }

  _handleAck(event) {
    const detail = event.detail || {}
    if (detail[this.subjectKeyValue] !== this.subjectIdValue) return
    // Hosts may echo a numeric comment_id (JSON round-trip): the DOM ids and
    // pending keys are strings. Coerce once so adopt + revert both hit.
    if (detail.comment_id != null) detail.comment_id = String(detail.comment_id)
    const region = this._toastRegion()
    const type = event.type

    if (CREATE_ACK_EVENTS.includes(type)) return this._createAck(detail, region)
    if (type === UPDATE_ACK_EVENT) return this._updateAck(detail, region)
    if (type === DELETE_ACK_EVENT) return this._deleteAck(detail, region)
    if (type === RESOLVE_ACK_EVENT) return this._resolveAck(detail, region)
    if (type === REACT_ACK_EVENT) return this._reactAck(detail, region)
    if (type === LOAD_MORE_ACK_EVENT) return this._loadMoreAck(detail, region)
  }

  _createAck(detail, region) {
    // The host echoes the submit's per-op `op_token` back on the ack (it rides
    // the event detail verbatim).
    const token = typeof detail.op_token === "string" && detail.op_token ? detail.op_token : null
    if (detail.ok) {
      // Accepted: the optimistic row stands in until the host re-render
      // replaces the list with the persisted comments (W4 semantics — only
      // the failure path removes it). The ok ack settles the WHOLE pending
      // set for this subject — no row is removed here, token or not.
      if (token) this._opBodies.delete(token)
      clearToasts(region)
      this._announce(this.savedMessageValue || "done")
    } else {
      // Failed: the failed op's row leaves and its draft body survives so the
      // user does not retype. The echoed token scopes the revert to exactly
      // one row — concurrent optimistic submits for this subject stand (their
      // own acks settle them). A tokenless (legacy W4) ack cannot be
      // attributed to an op, so it keeps the coarse sweep; a token whose row
      // is already gone (re-render swapped the list) removes nothing.
      if (token) {
        const row = this._optimisticRowByOp(token)
        if (row) {
          row.remove()
          this._restoreComposer(this._opBodies.get(token) || "")
          this._opBodies.delete(token)
        }
      } else {
        this._optimisticRows().forEach((node) => node.remove())
        this._restoreComposer(this._lastSubmittedBody || "")
      }
      showToast(region, this.errorMessageValue, "error")
      this._announce(this.errorMessageValue)
    }
    this._lastSubmittedBody = null
  }

  _updateAck(detail, region) {
    const id = detail.comment_id
    const pending = this._pending.get(id)
    const card = this._cardById(id)
    if (detail.ok) {
      const serverBody = detail.comment && detail.comment.body
      if (card && typeof serverBody === "string" && serverBody) {
        const body = card.querySelector("[data-work-comment-part='body']")
        if (body) body.textContent = serverBody
      }
      clearToasts(region)
    } else {
      // Revert to the pre-edit body (node replacement — never innerHTML) and
      // toast exactly once.
      if (card && pending && pending.bodyClone) {
        const body = card.querySelector("[data-work-comment-part='body']")
        if (body) body.replaceWith(pending.bodyClone.cloneNode(true))
      }
      showToast(region, this.updateErrorValue, "error")
    }
    this._pending.delete(id)
  }

  _deleteAck(detail, region) {
    const id = detail.comment_id
    const li = this._liById(id)
    if (detail.ok) {
      if (li) li.remove()
      clearToasts(region)
    } else {
      if (li) {
        li.removeAttribute("data-work-comment-removed")
        li.classList.remove("hidden")
      }
      showToast(region, this.deleteErrorValue, "error")
    }
  }

  _resolveAck(detail, region) {
    const id = detail.comment_id
    const pending = this._pending.get(`resolve:${id}`)
    const li = this._liById(id)
    if (detail.ok) {
      const serverResolved = detail.comment ? detail.comment.resolved : undefined
      if (li && typeof serverResolved === "boolean" && serverResolved !== (li.getAttribute("data-work-comment-resolved") === "true")) {
        this._applyResolved(li, serverResolved, pending ? pending.button : null)
      }
      clearToasts(region)
    } else {
      if (li && pending) this._applyResolved(li, pending.resolved, pending.button)
      showToast(region, this.resolveErrorValue, "error")
    }
    this._pending.delete(`resolve:${id}`)
  }

  _reactAck(detail, region) {
    const id = detail.comment_id
    const emoji = detail.emoji || ""
    const pending = this._pending.get(`react:${id}:${emoji}`)
    const li = this._liById(id)
    if (detail.ok) {
      // Adopt host-carried truth when provided (injected counts are host
      // truth — an absent payload keeps the optimistic count).
      if (li && Array.isArray(detail.reactions)) this._applyReactions(li, detail.reactions)
      clearToasts(region)
    } else {
      if (li && pending) {
        const button = this._reactButton(li, emoji)
        if (button) {
          button.setAttribute("data-work-comment-count", pending.count)
          button.setAttribute("aria-pressed", pending.pressed)
          const count = button.querySelector("[data-work-comment-reaction-count]")
          if (count) count.textContent = pending.count
          this._paintReaction(button, pending.pressed === "true")
        }
      }
      showToast(region, this.reactErrorValue, "error")
    }
    this._pending.delete(`react:${id}:${emoji}`)
  }

  // === internals: resolved / reactions state ===

  _applyResolved(li, resolved, button) {
    li.setAttribute("data-work-comment-resolved", resolved ? "true" : "false")
    const card = li.querySelector("[data-work-comment-card]")
    if (card) card.classList.toggle(MUTED_CLASS, resolved)
    const badge = li.querySelector("[data-work-comment-resolved-badge]")
    if (badge) badge.hidden = !resolved
    if (button) {
      button.textContent = resolved
        ? button.getAttribute("data-work-comment-unresolve-label")
        : button.getAttribute("data-work-comment-resolve-label")
    }
    // Resolve-while-filtered: a freshly resolved card must hide now (same
    // idiom as toggleFilter) — the chip's next toggle is not user-visible
    // feedback.
    if (this._filterOn && resolved && card) card.classList.add("hidden")
  }

  _resolveSnapshot(li) {
    // The snapshot carries the BUTTON ELEMENT (not its label text): the
    // revert path re-derives the label from the resolve/unresolve data
    // attributes, and a string here would crash the optimistic apply.
    const button = li.querySelector("[data-work-comment-action='resolve']")
    return {
      resolved: li.getAttribute("data-work-comment-resolved") === "true",
      button: button,
    }
  }

  _applyReaction(button, active) {
    const count = button.querySelector("[data-work-comment-reaction-count]")
    const current = parseInt(button.getAttribute("data-work-comment-count") || "0", 10)
    const next = active ? current + 1 : Math.max(0, current - 1)
    button.setAttribute("data-work-comment-count", String(next))
    button.setAttribute("aria-pressed", active ? "true" : "false")
    if (count) count.textContent = String(next)
    this._paintReaction(button, active)
  }

  _paintReaction(button, active) {
    button.classList.toggle("border-primary", active)
    button.classList.toggle("bg-primary/10", active)
    button.classList.toggle("text-primary", active)
    button.classList.toggle("border-border", !active)
    button.classList.toggle("text-foreground-medium", !active)
  }

  _applyReactions(li, serverReactions) {
    serverReactions.forEach((reaction) => {
      if (!reaction || typeof reaction.emoji !== "string") return
      // Iteration lookup — CSS.escape inside a quoted selector mangles emoji
      // (multi-byte code units escape with trailing spaces).
      const button = this._reactButton(li, reaction.emoji)
      if (!button) return
      const count = typeof reaction.count === "number" ? String(reaction.count) : null
      if (count !== null) {
        button.setAttribute("data-work-comment-count", count)
        const counter = button.querySelector("[data-work-comment-reaction-count]")
        if (counter) counter.textContent = count
      }
      if (typeof reaction.viewer_reacted === "boolean") {
        button.setAttribute("aria-pressed", reaction.viewer_reacted ? "true" : "false")
        this._paintReaction(button, reaction.viewer_reacted)
      }
    })
  }

  // === internals: paragraph anchors (read-only overlay — emits nothing) ===

  _handleDocumentClick(event) {
    const target = event.target
    if (!(target instanceof Element)) return
    const anchorId = target.getAttribute("id")
    if (!anchorId) return
    const card = this._cards().find((node) => node.getAttribute("data-work-comment-anchor") === anchorId)
    if (!card) return
    this._clearCardPaint()
    this._paint(card.querySelector("[data-work-comment-card]") || card, CARD_RING_CLASSES)
    card.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  _paragraphFor(anchorId) {
    if (!anchorId) return null
    const scope = this.anchorsSelectorValue ? document.querySelector(this.anchorsSelectorValue) : document
    if (!scope) return null
    return scope.querySelector(`#${CSS.escape(anchorId)}`)
  }

  _scrollTo(node) {
    if (node && typeof node.scrollIntoView === "function") {
      node.scrollIntoView({ behavior: "smooth", block: "center" })
    }
  }

  _paint(node, classes) {
    if (!node) return
    const added = classes.filter((name) => !node.classList.contains(name))
    added.forEach((name) => node.classList.add(name))
    this._painted.set(node, added)
  }

  _clearAllPaint() {
    this._painted.forEach((classes, node) => node.classList.remove(...classes))
    this._painted.clear()
  }

  _clearParagraphPaint() {
    // Drop paragraph washes only (the ring-inset marker — mirroring
    // _clearCardPaint's filter): a card ring from an anchor click is not
    // hover state and must survive hovering other cards.
    this._painted.forEach((classes, node) => {
      if (!classes.some((name) => name === "ring-inset")) return
      node.classList.remove(...classes)
      this._painted.delete(node)
    })
  }

  _clearCardPaint() {
    // Drop previous card rings, keep paragraph washes (hover state).
    this._painted.forEach((classes, node) => {
      if (classes.some((name) => name === "ring-inset")) return
      node.classList.remove(...classes)
      this._painted.delete(node)
    })
  }

  // === internals: editor + storage + dom helpers ===

  // The composer scope: on the generalized surface editable cards render
  // their OWN text editors ABOVE the composer, so every composer lookup must
  // scope to the composer wrapper (the `composer` target) — an
  // element-scoped first-editor lookup would read a card's edit shell
  // instead (dead submit/draft on any thread with an editable card).
  _composerScope() {
    return this.hasComposerTarget ? this.composerTarget : this.element
  }

  _toastRegion() {
    return toastRegionFor(this.element) || (this.element.querySelector("[data-work-comments-toast-region]") || null)
  }

  _cards() {
    return Array.from(this.element.querySelectorAll("li[data-work-comment-id]"))
  }

  _liById(id) {
    const match = this._cards().find((node) => node.getAttribute("data-work-comment-id") === id)
    return match || null
  }

  _reactButton(li, emoji) {
    const buttons = Array.from(li.querySelectorAll("[data-work-comment-action='react']"))
    return buttons.find((node) => node.getAttribute("data-work-comment-emoji") === emoji) || null
  }

  _cardById(id) {
    const li = this._liById(id)
    return li ? li.querySelector("[data-work-comment-card]") : null
  }

  _optimisticRows() {
    return Array.from(this.element.querySelectorAll("[data-work-comment-optimistic]"))
  }

  // The optimistic row minted by one submit, found by iteration over the
  // stamped rows (the kit's lookup idiom — never an interpolated selector).
  _optimisticRowByOp(token) {
    return this._optimisticRows().find((node) => node.getAttribute("data-work-comment-op") === token) || null
  }

  // Registry the failed-ack draft restore reads from: the op's OWN body, not
  // `_lastSubmittedBody` (which is whatever was submitted last — wrong for a
  // non-latest op failing while a newer one is still in flight).
  _rememberOp(token, body) {
    if (this._opBodies.size >= OP_CAP) {
      this._opBodies.delete(this._opBodies.keys().next().value)
    }
    this._opBodies.set(token, body)
  }

  _composerText() {
    return this._editorText(this._composerScope())
  }

  _editorText(scope) {
    return editorText(this.application, scope)
  }

  _clearComposer() {
    this._lastSubmittedBody = this._composerText().trim()
    clearEditor(this.application, this._composerScope())
    this._clearReply()
  }

  _focusComposer() {
    focusEditor(this.application, this._composerScope())
  }

  _draftKey() {
    return `${DRAFT_KEY_PREFIX}${this.subjectKeyValue}:${this.subjectIdValue}`
  }

  _saveDraft(text) {
    try {
      if (text) {
        window.localStorage.setItem(this._draftKey(), text)
      } else {
        window.localStorage.removeItem(this._draftKey())
      }
    } catch (_error) {
      // Storage may be unavailable (private window, cleared site data) — the
      // draft simply does not persist; never break composing.
    }
  }

  // A failed create restores the body EVERYWHERE the user sees it: the
  // persisted draft (storage) AND the on-screen composer editor (the submit
  // had emptied it — storage and screen must not disagree until a reload).
  // The composer is this controller's own target (`composer`), so the lookup
  // stays scoped — never document-wide. The guard is a USER-INPUT flag, not
  // a screen check: `draftChanged` marks real typing (the autosave has
  // already persisted it), a submit or a previous ack's restore clears it —
  // so concurrent-op failures chain correctly (each ack restores its own
  // op's body, the latest wins) while fresh user text is never clobbered on
  // screen or in storage. An empty body restores nothing anywhere. Caret
  // placement is the editor's own business — Lexxy selects the end of the
  // document whenever its value is written, so continued typing still follows
  // the restored body without this controller touching a Range.
  _restoreComposer(text) {
    if (!text) return
    if (this._composerTouched) return
    // Storage first: even if the composer was re-rendered away mid-ack (no
    // editor to write on screen), the failed body survives in the draft —
    // screen and storage must not disagree until a reload.
    this._saveDraft(text)
    setEditorText(this.application, this._composerScope(), text)
    this._composerTouched = false
  }

  _restoreDraft() {
    let draft = ""
    try {
      draft = window.localStorage.getItem(this._draftKey()) || ""
    } catch (_error) {
      return
    }
    if (!draft) return
    const scope = this._composerScope()
    // Never clobber what is already on screen: a restore only ever fills an
    // EMPTY composer.
    if (this._editorText(scope).trim()) return
    setEditorText(this.application, scope, draft)
    this._composerTouched = false // a programmatic restore, not user input
  }

  _nowLabel() {
    try {
      return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date())
    } catch (_error) {
      return ""
    }
  }

  _announce(text) {
    if (!this.hasLiveTarget) return
    this.liveTarget.textContent = text
  }
}
